import type { Stripe } from "@stripe/stripe-js";
import { ProductOrderActionsType } from "../components/product/selectProductOrder";
import { useOptions } from "../hooks/useOptions";
import { updateCart, log } from "./api";
import { fastUpdateProductQuantity } from "./store";
import { Product, ProductVariation } from "./types";
import { loadStripe } from "@stripe/stripe-js/pure";
import { IGatsbyImageData } from "gatsby-plugin-image";
import { SVG } from "@svgdotjs/svg.js"; // registerWindow
// import { createSVGWindow } from "svgdom";

// TEST
// export const stripeLoader: () => Promise<Stripe | null> = () =>
//   loadStripe(
//     "pk_test_51NNy06Kjw5xZIMAqgFQ6gEQ4yp1mc4VLh0nXZwlOS7rDh1Kh74bku53vWP5QgqPchkSBC8txQrOgt5VxGTHpkLkx00pFu3zMlV"
//   );

// LIVE
export const stripeLoader: () => Promise<Stripe | null> = () =>
    loadStripe(process.env.GATSBY_STRIPE);

export const formatPrice: (price: number) => string = (price: number) => {
    const options = useOptions().wpOptions.data;
    let decimals = typeof options.decimals === 'number' ? options.decimals : 2;
    return String((price / 100).toFixed(decimals));
};


export const getFormattedDate: (date: Date) => string = (date) =>
    date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });

export const decodeHtmlCharCodes = (str: string): string => {
    return str.replace(/(&#(\d+);)/g, function (match, capture, charCode) {
        return String.fromCharCode(charCode);
    });
};

export const productsReducer: (
    products: Product[],
    action: ProductOrderActionsType
) => Product[] = (products: Product[], action: ProductOrderActionsType) => {
    switch (action) {
        case "rating":
            return [
                ...products.sort(
                    (a, b) => Number(b.average_rating) - Number(a.average_rating)
                ),
            ];
        case "popularity":
            return [...products].sort((a, b) => a.originalIndex - b.originalIndex);
        case "latest":
            return [
                ...products.sort(
                    (a, b) =>
                        new Date(b.date_created).getTime() -
                        new Date(a.date_created).getTime()
                ),
            ];
        case "low_to_high":
            return [...products.sort((a, b) => Number(a.price) - Number(b.price))];
        case "high_to_low":
            return [...products.sort((a, b) => Number(b.price) - Number(a.price))];
        default:
            return products;
    }
};

export const updateProductQuantity: (
    key: string,
    quantity: number
) => Promise<void> = async (key, quantity) => {
    const body = {
        cart: {
            [key]: quantity,
        },
    };

    fastUpdateProductQuantity(key, quantity);

    await updateCart(body);
};

export const getAddressData = (
    target: EventTarget & HTMLFormElement,
    prefix_option: "billing" | "shipping",
    prefix_form: "billing" | "shipping"
) => {
    const addressData = {
        [`${prefix_option}_first_name`]: target[`${prefix_form}_first_name`].value,
        [`${prefix_option}_last_name`]: target[`${prefix_form}_last_name`].value,
        [`${prefix_option}_phone`]: target[`${prefix_form}_phone`].value,
        [`${prefix_option}_country`]: target[`${prefix_form}_country`].value,
        [`${prefix_option}_city`]: target[`${prefix_form}_city`].value,
        [`${prefix_option}_address_1`]: target[`${prefix_form}_address_1`].value,
        [`${prefix_option}_address_2`]: target[`${prefix_form}_address_2`].value,
        [`${prefix_option}_postcode`]: target[`${prefix_form}_postcode`].value,
    };

    const potentialStateField = target[`${prefix_form}_state`];
    if (potentialStateField) {
        addressData[`${prefix_option}_state`] = potentialStateField.value;
    }
    return addressData;
};

// In case we use the same billing address, copy it from the Shipping Info store
export const copyShippingToBilling = (
    shippingInfo: any,
    billingAddress: any
) => {
    const result = { ...billingAddress };

    for (const [key, value] of Object.entries(shippingInfo)) {
        if (key in ["order_comments", "billing_email"]) continue;

        const newKey = key.replace("shipping", "billing");
        result[newKey] = value;
    }

    return result;
};

export const getGatsbyImageData: (image: any) => IGatsbyImageData | null = (
    image
) => {
    return image?.childrenImageSharp?.length
        ? image?.childrenImageSharp[0]?.gatsbyImageData
        : null;
};

export const isTestPayment = () => {
    return document.cookie.includes('test_payment=1');
};

export const printPrice = (price) => {
    const options = useOptions().wpOptions.data;
    let decimals = typeof options.decimals === 'number' ? options.decimals : 2;
    return Number(price).toFixed(decimals);
};

export const getCheckoutData = (cart) => {
    if (!cart || !cart.products || cart.products.length === 0) return null;

    const isNotEmpty = Array.isArray(cart.products) && cart.products.length > 0;
    const products = isNotEmpty ? cart.products.filter((product) => !product.bundled_by) : [];

    const productsContent = products.reduce((acc, product) => {
        acc.ids.push(product.product_id);
        acc.names.push(product.name);
        acc.contents.push({
            item: product.product_id,
            quantity: product.quantity,
        });
        return acc;
    }, { ids: [], names: [], contents: [] });

    const data = {
        content_ids: productsContent.ids,
        content_name: productsContent.names,
        content_type: 'product',
        contents: productsContent.contents,
        num_items: products.length,
        value: cart.total || 0,
        currency: currency,
    };

    return data;
};

export const getPrices = (product: Product) => {
    if (product.variations_product.length === 0) {
        return {
            sale_price: product.sale_price ? printPrice(product.sale_price) : false,
            regular_price: printPrice(product.regular_price)
        }
    } else {

        let min_sale_price = Infinity;
        let max_sale_price = -Infinity;

        product.variations_product.forEach(variation => {
            if (variation.sale_price !== "") {
                const sale_price = parseFloat((variation.sale_price));
                min_sale_price = Math.min(sale_price, min_sale_price);
                max_sale_price = Math.max(sale_price, max_sale_price);
            }
        });

        const variation = product.variations_product.find(v =>
            v.id === product.defaultVariationID
        );

        if (variation) {
            return {
                sale_price: variation.sale_price ? printPrice(variation.sale_price) : false,
                regular_price: printPrice(variation.regular_price),
                min_sale_price: min_sale_price === Infinity ? printPrice(variation.sale_price) : printPrice(min_sale_price).toString(),
                max_sale_price: max_sale_price === -Infinity ? printPrice(variation.sale_price) : printPrice(max_sale_price).toString()
            }
        } else {
            const firstVariation = product.variations_product[0];
            return {
                sale_price: firstVariation.sale_price ? printPrice(firstVariation.sale_price) : false,
                regular_price: printPrice(firstVariation.regular_price),
                min_sale_price: min_sale_price === Infinity ? printPrice(firstVariation.sale_price) : printPrice(min_sale_price).toString(),
                max_sale_price: max_sale_price === -Infinity ? printPrice(firstVariation.sale_price) : printPrice(max_sale_price).toString()
            }
        }
    }
}

export const getParam = (name: string) => {
    let results = new RegExp('[\?&]' + name + '=([^&#]*)').exec(window.location.href);
    if (results && results.length) {
        return results[1];
    }
}

export const gtag = (type: string, source: string, data: any = {}) => {
    if (typeof window !== "undefined" && !!window?.gtag) {
        if (data) {
            window.gtag(type, source, data);
        } else {
            window.gtag(type, source);
        }
        log('gtag', `${type}-${source}`, data);
    }
}